import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Password as PasswordIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';
import { organizationsApi } from '../../services/api';

const CreateManagerModal = ({ open, onClose, onSuccess, organization }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    temporaryPassword: '',
    sendWelcomeEmail: true
  });

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setFormData({
        username: '',
        email: '',
        full_name: '',
        temporaryPassword: '',
        sendWelcomeEmail: true
      });
      setError('');
      setGeneratedPassword('');
      setShowPassword(false);
      onClose();
    }
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const generatePassword = () => {
    // Generate a secure temporary password
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one character from each category
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special
    
    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData(prev => ({ ...prev, temporaryPassword: password }));
    setGeneratedPassword(password);
    setShowPassword(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const validateForm = () => {
    if (!formData.username.trim()) return 'Username is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.full_name.trim()) return 'Full name is required';
    if (!formData.email.includes('@')) return 'Please enter a valid email address';
    if (formData.username.length < 3) return 'Username must be at least 3 characters';
    return null;
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (validation) {
      setError(validation);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await organizationsApi.createManager({
        ...formData,
        organizationId: organization.id
      });

      // Show success and pass the created manager data
      onSuccess({
        manager: response.data.manager,
        temporaryPassword: response.data.temporaryPassword,
        organization: response.data.organization
      });

      handleClose();
    } catch (err) {
      console.error('Error creating manager:', err);
      setError(err.response?.data?.error || 'Failed to create manager account');
    } finally {
      setLoading(false);
    }
  };

  if (!organization) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <PersonAddIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Create Manager Account</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Organization Info */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary.main">
                {organization.name}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Creating a manager account for this organization
            </Typography>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={handleChange('full_name')}
              disabled={loading}
              required
              helperText="Manager's full name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              disabled={loading}
              required
              helperText="Will be used for login and notifications"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={handleChange('username')}
              disabled={loading}
              required
              helperText="Unique username for login"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* Role Display */}
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Role
              </Typography>
              <Chip label="Manager" color="warning" variant="outlined" />
              <Typography variant="caption" display="block" mt={1} color="text.secondary">
                Organization-wide management permissions
              </Typography>
            </Box>
          </Grid>

          {/* Password Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Temporary Password
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Temporary Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.temporaryPassword}
              onChange={handleChange('temporaryPassword')}
              disabled={loading}
              helperText="Leave empty to auto-generate a secure password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PasswordIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box display="flex">
                      <Tooltip title="Generate secure password">
                        <IconButton onClick={generatePassword} disabled={loading}>
                          <GenerateIcon />
                        </IconButton>
                      </Tooltip>
                      {formData.temporaryPassword && (
                        <>
                          <Tooltip title="Toggle password visibility">
                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy password">
                            <IconButton onClick={() => copyToClipboard(formData.temporaryPassword)}>
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Email Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Email Settings
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sendWelcomeEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, sendWelcomeEmail: e.target.checked }))}
                  disabled={loading}
                />
              }
              label="Send welcome email with login instructions"
            />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {formData.sendWelcomeEmail 
                ? "Manager will receive an email with their login credentials and setup instructions"
                : "You'll need to manually provide the login credentials to the manager"
              }
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={<PersonAddIcon />}
        >
          {loading ? 'Creating...' : 'Create Manager'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateManagerModal; 